import { Navigate } from "react-router-dom";

import { AppPagePaths } from "App/routes/paths";
import { lazyWithRetries } from "Utils/LazyWithRetries/LazyWithRetries";

import LiveOpsRoutingPage from "./liveOpsRoutingPage";
import { liveOpsPathResources } from "./Pages/liveOpsPaths";
import { taskPathResources } from "./Pages/TasksPage/Pages/taskPaths";
import { taskRoutes } from "./Pages/TasksPage/TaskRoutes";

import type { RegalRouteObject } from "App/routes/types";

export type LiveOpsFlags = {
    showTasks: boolean;
    showAgents: boolean;
    showQueues: boolean;
    showRecentActivity: boolean;
};

const AgentsIndex = lazyWithRetries("Agents Index", () => import("./Pages/AgentsPage/AgentsPage"));
const QueuesIndex = lazyWithRetries("Queues page", () => import("./Pages/QueuesPage/QueuesPage"));
const RecentActivityPage = lazyWithRetries(
    "Recent activity page",
    () => import("./Pages/RecentActivityPage/RecentActivityPage")
);

/**
 * Determines the default redirect path based on available feature flags.
 * Returns the first available page in priority order.
 */
export const getDefaultLiveOpsPath = (flags: LiveOpsFlags): string => {
    const { showTasks, showAgents, showQueues, showRecentActivity } = flags;

    // Priority order matches the navigation menu order
    if (showTasks) {
        return `${AppPagePaths.liveOps}/${liveOpsPathResources.tasks}/${taskPathResources.upcoming}`;
    }
    if (showAgents) {
        return `${AppPagePaths.liveOps}/${liveOpsPathResources.agents}`;
    }
    if (showQueues) {
        return `${AppPagePaths.liveOps}/${liveOpsPathResources.queues}`;
    }
    if (showRecentActivity) {
        return `${AppPagePaths.liveOps}/${liveOpsPathResources.recentActivity}`;
    }

    // Fallback to root
    return `/`;
};

export const getLiveOpsRoute = (flags: LiveOpsFlags): RegalRouteObject => {
    const defaultPath = getDefaultLiveOpsPath(flags);

    return {
        path: AppPagePaths.liveOps,
        element: <LiveOpsRoutingPage />,
        hasNestedRoutes: true,
        children: [
            {
                index: true,
                element: <Navigate to={defaultPath} replace />,
            },
            {
                path: liveOpsPathResources.agents,
                element: <AgentsIndex />,
            },
            {
                path: `${liveOpsPathResources.tasks}/*`,
                ...taskRoutes,
            },
            {
                path: liveOpsPathResources.queues,
                element: <QueuesIndex />,
            },
            {
                path: liveOpsPathResources.recentActivity,
                element: <RecentActivityPage />,
            },
        ],
    };
};

export { rerouteAgentsRoute } from "./Pages/AgentsPage/AgentsRoute";
export { rerouteTasksRoute, rerouteRecentTasksRoute } from "./Pages/TasksPage/TaskRoutes";
export { rerouteRecentActivityRoute } from "./Pages/RecentActivityPage/RecentActivityRoutes";
export { rerouteQueuesRoute } from "./Pages/QueuesPage/QueuesRoutes";
